import React, {useContext, useRef, useState} from "react";
import i18n from "../../../i18n";
import {VisitedContext} from "@/VisitedContext";
import {useTranslation, withTranslation} from 'react-i18next';
import {SettingsContext} from "@/SettingsContext";

export default function AudiosyncBlock({ item })
{
    // Ref for socket
    const socket = useRef(null);
    const reconnectTimeout = useRef(null);

    // Ref for audio
    const audioRef = useRef(null);

    // State
    const [videoPlaying, setVideoPlaying] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [deviceId, setDeviceId] = useState(null);
    const [mediaFile, setMediaFile] = useState(null);
    const [mimeType, setMimeType] = useState(null);
    const [duration, setDuration] = useState(0);
    const [played, setPlayed] = useState(0);
    const [startTime, setStartTime] = useState(0);

    const [readyState, setReadyState] = useState(WebSocket.CLOSED);

    const progressWidth = { width: `${(played*100).toFixed(3)}%` };
    const { t } = useTranslation();
    const { tourSettings } = useContext(SettingsContext);
    const visited = useContext(VisitedContext);

    React.useEffect(() => {
        const fetchMediaData = () => {
            let mimeTypeName = item.data['media'][i18n.language]['mime_type'] ? item.data['media'][i18n.language]['mime_type']['name'] : null;
            let mimeType = 'audio/'+mimeTypeName;
            setMimeType(mimeType);
            if (mimeTypeName && item.data['media'] && item.data['media'][i18n.language]) {
                setMediaFile( item.data['media'] && item.data['media'][i18n.language].preview_url);
            }
        }

        if (item.data['media'] && item.data['media'][i18n.language]) {
            fetchMediaData();
        }

        // Initialize WebSocket
        connectWebSocket();

        // Cleanup the WebSocket when the component unmounts
        return () => {
            if (socket.current) {
                socket.current.close(1000,'leaving page');
                console.log('close WebSocket (leave page)');
            }

            if (reconnectTimeout.current) {
                clearTimeout(reconnectTimeout.current);
            }
        };

    }, [item])

    const handleListen = () => {

        console.log("handleListen. send startAudio and start the player if not started.");
        socket.current.send(JSON.stringify({ event: 'startAudio', deviceId: deviceId}));

        if (audioRef.current.paused) {
            handlePlay();
        }
    }

    const handleStop = () => {
        console.log('handleStop:stop playing.');

        audioRef.current.currentTime = 0;
        setVideoPlaying(false);

        handlePause();

    }

    const handlePause = () => {
        console.log('handlePause: pause playing.')

        if (!audioRef.current.paused) {
            audioRef.current.pause();
        }
        setPlaying(false);
    }

    const handlePlay = () => {
        console.log('handlePlay:start playing.');

        if (audioRef.current) {
            audioRef.current.play().then(() => {
                audioRef.current.currentTime = parseFloat(startTime);
                setPlaying(true);
            }).catch(err => {
                console.error("Error starting playback: ", err)
                setPlaying(false);
            });
        } else {
            setPlaying(false);
        }
    }

    const connectWebSocket = () => {

        let itemDataDeviceId = item.data['device'] && item.data['device']['device_id'] ? item.data['device']['device_id'] : null;
        setDeviceId(itemDataDeviceId);

        console.log("connectWebSocket");
        console.log("name", visited?.variables?.name);
        console.log("item device_id", itemDataDeviceId);
        console.log("state device_id", deviceId);

        console.log(`trying to connect... url: ${process.env.AUDIOSYNC_WEBSOCKET_URL}`);

        // Initialize the WebSocket
        socket.current = new WebSocket(process.env.AUDIOSYNC_WEBSOCKET_URL);
        setReadyState(WebSocket.CONNECTING);

        // Add event listeners, if needed
        socket.current.onopen = () => {
            console.log('WebSocket connected', item);
            setReadyState(WebSocket.OPEN);

            // register listener
            if (itemDataDeviceId) {
                socket.current.send(JSON.stringify({type: 'listen', deviceId: itemDataDeviceId}));
            }

            if (Object.keys(visited?.variables).includes('name')) {
                socket.current.send(JSON.stringify({"name": visited.variables.name}));
            }
        };
        socket.current.onerror = (error) => {
            console.error('WebSocket error:', error);
        };
        socket.current.onclose = (event) => {
            console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`);
            setReadyState(WebSocket.CLOSED);
            setPlaying(false);

            // Handle reconnection
            if (event.code !== 1000) { // Don't reconnect on normal closure
                console.log('Reconnecting WebSocket in 1 second...');
                reconnectTimeout.current = setTimeout(connectWebSocket, 1000); // Reconnect after 1 second
            } else {
                console.log('Reconnect not required');
            }
        };

        socket.current.onmessage = function(event) {
            const message = JSON.parse(event.data);

            console.log("received message: ", message);

            if (message.type === 'pause') {
                handlePause();
            }

            if (message.event === 'waiting') {
                console.log("Waiting for video to start");
            }

            if (message.event === 'videoStarted') {
                // setVideoPlaying(true);
                // if (message.elapsedTime) {
                //     // we received a timecode.
                //     console.log("Received timecode. Update start position to " + parseFloat(message.elapsedTime) + " seconds");
                //
                //     //setStartTime(parseFloat(message.elapsedTime));
                // }
            }

            if (message.event === 'currentTime') {
                setVideoPlaying(true);
                if (message.elapsedTime) {
                    // we received a timecode.
                    console.log("Received timecode. Update start position to " + parseFloat(message.elapsedTime) + " seconds");
                    setStartTime(parseFloat(message.elapsedTime));
                }
            }

            if (message.event === 'videoStopped') {
                handleStop();
            }
        }
    }

    const onLoadedMetadata = (event) => {
        console.log("audio metadata loaded",event.target.duration);
        setDuration(event.target.duration);
    }

    const onTimeUpdate = (event) => {
        let newPlayed = 0;
        if (event.target.currentTime) {
            newPlayed = event.target.currentTime / event.target.duration;
        }
        setPlayed(newPlayed);
    }

    const Duration = ({ className, seconds }) => {
        return (
            <time dateTime={`P${Math.round(seconds)}S`} className={className}>
                {format(seconds)}
            </time>
        )
    }

    const format = (seconds) => {
        const date = new Date(seconds * 1000)
        const hh = date.getUTCHours()
        const mm = date.getUTCMinutes()
        const ss = pad(date.getUTCSeconds())
        if (hh) {
            return `${hh}:${pad(mm)}:${ss}`
        }
        return `${mm}:${ss}`
    }

    const pad = (string) => {
        return ('0' + string).slice(-2)
    }



        return (
            <div className="">

                {mediaFile && mimeType &&
                    <audio className="hidden" ref={audioRef}
                           onLoadedMetadata={onLoadedMetadata}
                           onTimeUpdate={onTimeUpdate}
                           onPause={handlePause}
                        >
                        <source src={mediaFile} type={mimeType}/>
                    </audio>
                }

                {item && mediaFile && mimeType &&
                    <>

                        <div className="flex flex-col w-full items-center my-5">

                            {!playing &&
                                <button onClick={handleListen} disabled={!videoPlaying || startTime === 0}
                                        style={{"backgroundColor": `${ tourSettings.theme_color }`, "color": `${ tourSettings.theme_text_color }` }}
                                        className={`flex relative mx-auto my-5 py-2 px-6 rounded-full ${!videoPlaying ? 'opacity-40' : 'opacity-100'}`}>
                                    <div className={`absolute w-3 h-3 -mt-3.5 right-4 rounded-full ${readyState === 1 && deviceId ? 'bg-green-800' : 'bg-red-800'}`}/>
                                    {t('button.listen')}
                                </button>
                            }

                            {playing &&
                                <button onClick={handlePause} disabled={!videoPlaying || startTime === 0}
                                        style={{"backgroundColor": `${ tourSettings.theme_color }`, "color": `${ tourSettings.theme_text_color }` }}
                                        className={`flex mx-auto my-5 py-2 px-6 rounded-full ${!playing ? 'opacity-40' : 'opacity-100'}`}>{t('button.pause')}</button>
                            }


                        </div>
                        {playing &&
                            <div className="flex w-full">
                                <div className="flex w-12">
                                    <Duration seconds={duration * played}/>
                                </div>
                                <div className="flex flex-1 mx-2">
                                    <div className="progress">
                                        <div className="progress-done bg-[#EE7203]" style={progressWidth}/>
                                    </div>
                                </div>
                                <div className="flex w-12">
                                    <Duration seconds={duration}/>
                                </div>
                            </div>
                        }

                    </>
                }
            </div>
        );

}


